import { useEffect, useRef, useState } from "react";
import Alert from "@material-ui/lab/Alert";
import { makeStyles } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

const AUTO_HIDE_MS = 5000;

const useStyles = makeStyles(() => ({
  "@keyframes snackIn": {
    from: { opacity: 0, transform: "translateY(8px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  stack: {
    position: "fixed",
    right: "24px",
    top: "100px",
    zIndex: 1400,
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    alignItems: "flex-end",
    pointerEvents: "none",
    "& > *": {
      pointerEvents: "auto",
    },
  },
  alert: {
    position: "relative",
    overflow: "hidden",
    minWidth: "320px",
    maxWidth: "440px",
    backgroundColor: "#FFFFFF",
    color: "#1A1E24",
    borderRadius: "8px",
    border: "1px solid #E1E6F1",
    borderLeftWidth: "4px",
    padding: "10px 16px",
    fontSize: "14px",
    fontWeight: 500,
    alignItems: "center",
    boxShadow: "0 6px 20px rgba(26, 30, 36, 0.14)",
    animation: "$snackIn 180ms ease-out",
    "& .MuiAlert-icon": {
      padding: 0,
      marginRight: "12px",
    },
    "& .MuiAlert-message": {
      padding: 0,
      color: "#1A1E24",
      whiteSpace: "pre-line",
    },
    "& .MuiAlert-action": {
      paddingLeft: "12px",
      marginRight: "-6px",
      color: "#A1ADBA",
    },
  },
  progress: {
    position: "absolute",
    left: 0,
    bottom: 0,
    display: "block",
    width: "100%",
    height: "2px",
    borderBottomLeftRadius: "8px",
    backgroundColor: "currentColor",
    transformOrigin: "left center",
  },
  success: {
    borderLeftColor: "#43A56C",
    "& .MuiAlert-icon": { color: "#43A56C" },
    "& $progress": { backgroundColor: "#43A56C" },
  },
  error: {
    borderLeftColor: "#EE294C",
    "& .MuiAlert-icon": { color: "#EE294C" },
    "& $progress": { backgroundColor: "#EE294C" },
  },
  info: {
    borderLeftColor: "#5195E6",
    "& .MuiAlert-icon": { color: "#5195E6" },
    "& $progress": { backgroundColor: "#5195E6" },
  },
}));

const SnackItem = ({ note, classes, onClose }) => {
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState(AUTO_HIDE_MS);
  const pausedRef = useRef(false);

  useEffect(() => {
    let frame;
    let last = performance.now();

    const tick = (now) => {
      const delta = now - last;
      last = now;
      if (!pausedRef.current) {
        setRemaining((prev) => {
          const next = prev - delta;
          if (next <= 0) {
            onClose();
            return 0;
          }
          return next;
        });
      }
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const severity = note.severity || "info";
  const message = (note.message && t(note.message)) || note.description || "";
  const ratio = 1 - Math.max(0, Math.min(1, remaining / AUTO_HIDE_MS));

  return (
    <Alert
      severity={severity}
      onClose={onClose}
      className={`${classes.alert} ${classes[severity] || classes.info}`}
      onMouseEnter={() => {
        pausedRef.current = true;
      }}
      onMouseLeave={() => {
        pausedRef.current = false;
      }}
    >
      {message}
      <span
        className={classes.progress}
        style={{ transform: `scaleX(${ratio})` }}
      />
    </Alert>
  );
};

const Snackbar = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const notifications = useSelector((state) => state.snackbar.notifications);

  if (!notifications.length) return null;

  const remove = (key) =>
    dispatch({ type: "SET_SNACKBAR", payload: { remove: key } });

  return (
    <div className={classes.stack}>
      {notifications.map((note) => (
        <SnackItem
          key={note.key}
          note={note}
          classes={classes}
          onClose={() => remove(note.key)}
        />
      ))}
    </div>
  );
};

export default Snackbar;
